import { RouterProvider } from '@tanstack/react-router';
import {
  cacheExchange,
  Client,
  fetchExchange,
  mapExchange,
  Provider as UrqlProvider,
} from 'urql';
import { useEffect, useMemo, useRef } from 'react';
import { KeycloakProvider, useKeycloak } from './auth/KeycloakProvider.tsx';

import {
  IntlProvider,
  load,
  LocalizationProvider,
} from '@progress/kendo-react-intl';

import LikelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import CurrencyData from 'cldr-core/supplemental/currencyData.json';
import WeekData from 'cldr-core/supplemental/weekData.json';
import Numbers from 'cldr-numbers-full/main/de/numbers.json';
import Currencies from 'cldr-numbers-full/main/de/currencies.json';
import DateFields from 'cldr-dates-full/main/de/dateFields.json';
import CaGregorian from 'cldr-dates-full/main/de/ca-gregorian.json';
import TimeZoneNames from 'cldr-dates-full/main/de/timeZoneNames.json';

import { router } from './router.tsx';
import { getBackendUrl } from './helpers/backendUrl';

load(
  LikelySubtags,
  CurrencyData,
  WeekData,
  Numbers,
  Currencies,
  DateFields,
  CaGregorian,
  TimeZoneNames,
);

function useUrqlClient() {
  const { token } = useKeycloak();
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // tokenRef.current is read inside fetchOptions, which urql invokes per
  // request — never during render. The lint rule can't see across the
  // closure boundary, so we silence the false positive here.
  return useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    return new Client({
      url: getBackendUrl() + import.meta.env.VITE_GRAPHQL_ENDPOINT,
      fetchOptions: () => {
        const headers: Record<string, string> = {
          'content-type': 'application/json',
        };
        if (tokenRef.current) {
          headers['Authorization'] = `Bearer ${tokenRef.current}`;
        }
        return { headers };
      },
      preferGetMethod: false,
      exchanges: [
        cacheExchange,
        mapExchange({
          onError(error) {
            console.error(error);
          },
        }),
        fetchExchange,
      ],
      requestPolicy: 'network-only',
    });
  }, []);
}

const AppInner: React.FC = () => {
  const client = useUrqlClient();
  return (
    <UrqlProvider value={client}>
      <IntlProvider locale={'de'}>
        <LocalizationProvider language={'de-DE'}>
          <RouterProvider router={router} />
        </LocalizationProvider>
      </IntlProvider>
    </UrqlProvider>
  );
};

export const App: React.FC = () => {
  return (
    <KeycloakProvider>
      <AppInner />
    </KeycloakProvider>
  );
};
